import React from "react";
import { useFormik } from "formik";
import {
  TextField,
  Button,
  Grid,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from "@mui/material";
import * as Yup from "yup";
import { TeamMember } from "../../types/TeamModel";
import SendIcon from "@mui/icons-material/Send";
import { sendInviteEmail } from "../../services/teamMemberService";
import { enqueueSnackbar } from "notistack";
import { useHandleError } from "../../hooks/useHandleError";

export interface TeamMemberFormProps {
  selectedRow?: TeamMember;
  onSubmit: (values: TeamMember) => void;
}

export const TeamMemberEntryForm: React.FC<TeamMemberFormProps> = ({
  selectedRow,
  onSubmit,
}) => {
  const handleError = useHandleError();
  const formik = useFormik({
    initialValues: {
      email: selectedRow?.email || "",
      role: selectedRow?.role || "EDITOR",
      firstName: selectedRow?.firstName || null,
      lastName: selectedRow?.lastName || null,
      mobile: selectedRow?.mobile || null,
      city: selectedRow?.city || null,
      country: selectedRow?.country || null,
      ...selectedRow,
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      email: Yup.string().email("Invalid email format").required("Required"),
      role: Yup.string().required("Role is required"),
      firstName: Yup.string().nullable(),
      lastName: Yup.string().nullable(),
      mobile: Yup.string().nullable(),
      city: Yup.string().nullable(),
      country: Yup.string().nullable(),
    }),
    onSubmit: (values: TeamMember) => {
      console.log(values, "VALUES");
      onSubmit(values);
    },
  });

  const handleInvite = async () => {
    if (!selectedRow?.email) {
      enqueueSnackbar(
        `Something went wrong, id is missing, please contact admin.`,
        {
          variant: "error",
        }
      );
      return;
    }

    sendInviteEmail(formik.values)
      .then(() => {
        enqueueSnackbar(
          `Invitation has been sent to ${formik.values.email} successfully`,
          {
            variant: "success",
          }
        );
      })
      .catch((error: any) => {
        handleError(error);
      });
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      <Grid
        item
        xs={12}
        style={{
          padding: "1rem 0",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <div></div>
        <div>
          {(formik.values.status === "CREATED" ||
            formik.values.status === "INVITATION SENT") && (
            <Button
              size="small"
              color="primary"
              variant="outlined"
              endIcon={<SendIcon />}
              onClick={handleInvite}
            >
              Invite
            </Button>
          )}
        </div>

        <div>
          <Button
            size="small"
            color="primary"
            variant="outlined"
            endIcon={<SendIcon />}
            onClick={handleInvite}
          >
            TEST Invite
          </Button>
        </div>
      </Grid>
      <Grid container spacing={3}>
        {/* Email Field */}
        <Grid item xs={12}>
          <TextField
            size="small"
            fullWidth
            autoComplete="off"
            id="email"
            name="email"
            label="Email"
            value={formik.values.email}
            onChange={formik.handleChange}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
            disabled={!!selectedRow?._id}
          />
        </Grid>

        {/* Role Field */}
        <Grid item xs={12}>
          <FormControl fullWidth size="small">
            <InputLabel id="role-label">Role</InputLabel>
            <Select
              labelId="role-label"
              id="role"
              name="role"
              value={formik.values.role}
              onChange={formik.handleChange}
              label="Role"
            >
              <MenuItem value="ADMIN">Admin</MenuItem>
              <MenuItem value="EDITOR">Editor</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* First Name Field */}
        <Grid item xs={12} md={6}>
          <TextField
            size="small"
            fullWidth
            autoComplete="off"
            id="firstName"
            name="firstName"
            label="First Name"
            value={formik.values.firstName}
            onChange={formik.handleChange}
          />
        </Grid>

        {/* Last Name Field */}
        <Grid item xs={12} md={6}>
          <TextField
            size="small"
            fullWidth
            autoComplete="off"
            id="lastName"
            name="lastName"
            label="Last Name"
            value={formik.values.lastName}
            onChange={formik.handleChange}
          />
        </Grid>

        {/* Mobile Field */}
        <Grid item xs={12}>
          <TextField
            size="small"
            fullWidth
            autoComplete="off"
            id="mobile"
            name="mobile"
            label="Mobile"
            value={formik.values.mobile}
            onChange={formik.handleChange}
          />
        </Grid>

        {/* City Field */}
        <Grid item xs={12} md={6}>
          <TextField
            size="small"
            fullWidth
            autoComplete="off"
            id="city"
            name="city"
            label="City"
            value={formik.values.city}
            onChange={formik.handleChange}
          />
        </Grid>

        {/* Country Field */}
        <Grid item xs={12} md={6}>
          <TextField
            size="small"
            fullWidth
            autoComplete="off"
            id="country"
            name="country"
            label="Country"
            value={formik.values.country}
            onChange={formik.handleChange}
          />
        </Grid>

        {/* Footer Grid for Submission Button */}
        <Grid container spacing={3}>
          {/* Other items... */}

          {/* Submission Button Grid Item */}
          <Grid
            item
            xs={12}
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "1.5rem",
            }}
          >
            <Button
              size="small"
              color="primary"
              variant="contained"
              type="submit"
            >
              {selectedRow?._id ? "Update" : "Add New"} Team Member
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </form>
  );
};
